// All copy/data lives here so backend integration is trivial later.

export const NAME = 'Sirin Thamakaison';
export const DOMAIN = 'heysirin.com';
export const EMAIL = 'n.thamakaison@gmail.com';

export const STATUS_LINE = {
  location: 'Bangkok · Boston',
  now: [
    'shipping a new launch for Crying Cloud',
    'prototyping a small GTM tool',
    'baking a pistachio batch at BKKButter',
    'editing a short film for Nancreated',
  ],
};

export const HERO_GREETING = 'Hi, I\'m Sirin.';
export const HERO_SUBTITLE = 'I build products, systems, and stories. This is where I share what that looks like.';

// Prompts for the conversational hero. Featured on load = index 2 ("what do you do").
export const HERO_PROMPTS = [
  {
    chip: 'about you',
    q: 'Tell me about you.',
    a: 'I\'m someone who builds things I once needed.',
  },
  {
    chip: 'what you built',
    q: 'What have you built?',
    a: 'A mental health platform that now reaches over 100,000 young people. A medical app with 200k downloads. A small bakery. A few short films. And a few spaces where people feel less alone.',
  },
  {
    chip: 'what you do',
    q: 'What do you do?',
    a: 'I turn messy, personal problems into things that actually work for other people. Sometimes that looks like product. Sometimes it is code at 2am. Sometimes it is figuring out why something good is not growing. I stay with the problem until it works.',
  },
  {
    chip: 'why this name',
    q: 'Why this name?',
    a: 'It\'s my own name. I wanted a place where I don\'t have to split who I am from what I make.',
  },
];

export const WHAT_I_DO_HEADER = {
  kicker: '\u00a7 01 \u00b7 What I do',
  title: 'Three roles, but it is really one habit.',
  description:
    'I never learned how to separate building, deciding, and shipping. If something is not working, I would rather follow it all the way through than hand it off halfway. That is why my work looks like three different jobs.',
};

export const WHAT_I_DO = [
  {
    label: 'build',
    role: 'GTM Engineer',
    blurb:
      'I build the parts people do not think about until they break. How leads move, how data connects, where things quietly leak. Most growth problems are not strategy problems. They are systems problems.',
    tools: ['Segment', 'Hightouch', 'Attio', 'Python', 'n8n', 'Postgres'],
  },
  {
    label: 'shape',
    role: 'Product Manager',
    blurb:
      'I spend time figuring out what people actually mean, not just what they say. The interesting decisions are never clean. They sit in tradeoffs that do not fit neatly into a ticket.',
    tools: ['Figma', 'Linear', 'Amplitude', 'Notion', 'Loom'],
  },
  {
    label: 'ship',
    role: 'Engineer',
    blurb:
      'I build things end to end because it is faster than waiting. I care more about feedback loops than perfect stacks. I just want to know if it works.',
    tools: ['React', 'TypeScript', 'Vite', 'FastAPI', 'Postgres', 'Supabase'],
  },
];

export const PROJECTS = [
  {
    id: 'crying-cloud',
    name: 'Crying Cloud',
    kicker: 'December 2020 - Present · Co-founder, Product & Growth',
    tagline: 'I spent over 4,000 hours escaping into a game. Crying Cloud came from finally understanding why I needed it.',
    context:
      'It started from a personal place, not a plan. I knew what it felt like to need someone and not have them. So I built something that made it easier to reach help. Over time, it became a platform connecting thousands of young people in Thailand to therapy. The part I did not expect was how many people were quietly waiting.',
    outcome: [
      '100,000+ young people reached',
      '7,000+ routed to therapy',
      'Partnered with the Thai Department of Mental Health',
    ],
    role: 'Co-founder · PM · GTM · Engineer',
    url: 'https://cryingcloud.org',
    embed: 'https://cryingcloud.org',
    instagram: 'https://www.instagram.com/cryingcloud_th/',
    accent: '#2E4E3C',
  },
  {
    id: 'withu',
    name: 'WithU',
    kicker: 'July 2022 - August 2023 · Co-founder, Product Manager',
    tagline: 'Built for patients who fall through the cracks. Not because they do not care, but because systems were not built with them in mind.',
    context:
      'I co-founded and led product on WithU, a telemedicine and care-navigation app. I worked on the onboarding flow, the doctor-matching logic, and the handoff between offline and online care. Most of the time was spent listening to clinicians and patients, then trying to translate what they meant into something a screen could carry.',
    outcome: [
      '200,000+ downloads',
      '#1 in Medical (TH App Store)',
      '4.8 stars on average',
    ],
    role: 'Co-founder · PM',
    url: 'https://apps.apple.com/th/app/withu/id1634761458',
    embed: null,
    accent: '#6B4423',
  },
  {
    id: 'foundess',
    name: 'Foundess',
    kicker: 'June 2025 - August 2025 · Creative Director',
    tagline: 'I wanted to be in rooms where women were building real things. So I helped create those rooms.',
    context:
      'I led creative direction across events, editorial, and digital. I wrote the brand voice, shaped the event format, and helped grow the community from a private chat into something early-stage operators recognized. The work I am most proud of is the part that does not show up in a brand book: the small decisions about who to invite, what to put on the table, when to leave a silence.',
    outcome: [
      'Audience grew 5x in 9 months',
      'Events in Boston, NYC, and SF',
      'Brand system adopted org-wide',
    ],
    role: 'Creative Director · Brand · Events',
    url: 'https://foundess.com',
    embed: 'https://foundess.com',
    accent: '#A8593A',
  },
  {
    id: 'youth-neuro',
    name: 'Youth Neuro',
    kicker: '2022 · Contributor',
    tagline: 'Writing about neuroscience for teenagers who are probably just trying to understand themselves.',
    context:
      'I contributed editorial and web work during undergrad. A small project, but a useful reminder that clear writing is a product feature. The audience was always going to read it on a phone in a hallway between classes. That changes everything.',
    outcome: [
      'Editorial contributor',
      'Web refresh',
    ],
    role: 'Editorial · Web',
    url: 'https://youthneuro.org',
    embed: 'https://youthneuro.org',
    accent: '#4A5D42',
  },
];

export const BKKBUTTER = {
  name: 'BKKButter',
  tagline: 'A bakery that taught me how people decide.',
  kicker: 'Macaron studio · Bangkok · July 2020 - July 2023',
  link: 'https://www.instagram.com/bkkbutter/',
  linkLabel: '@bkkbutter',
  sub:
    'Small batches. Experimental flavors. Built to fundraise for Myanmar refugees.',
  heroImage:
    'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/3azgm29j_DSC04813.JPG',
  secondaryImage:
    'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/vd5vyqcl_DSC05093.JPG',
  stats: [
    { label: 'organic reach', value: '2M+', unit: 'organic reach across content' },
    { label: 'impact', value: '$10K+', unit: 'raised for Myanmar refugees' },
  ],
  quote: {
    body:
      'I started baking Korean macarons at 14 to fundraise for Myanmar refugees. Bangkok heat was hard, but earning trust online was harder. After three years of testing content, packaging, and messaging, I learned one thing: people do not buy things, they buy feelings.',
    emphasis: 'People do not buy things, they buy feelings.',
    attribution: 'Sirin, on building BKKButter',
  },
  flavors: [
    {
      name: 'Korean macarons (traditional almond-based shells with a generous dollop of buttercream)',
      color: '#A8593A',
    },
    {
      name: 'Seasonal macaron cereal (tiny macaron shells shaped as eggs, avocados, hearts, etc.)',
      color: '#6B8E4A',
    },
    {
      name: 'Buttercream jars (delicious creamy dip for macaron cereal)',
      color: '#C9A06C',
    },
  ],
  pressNote: 'Built to fundraise for Myanmar refugees',
  instagram: {
    handle: 'bkkbutter',
    displayName: 'BKKButter',
    bio: 'small-batch macarons \u00b7 bangkok\nfundraising for Myanmar refugees\npre-order \u2192 bio link',
    posts: '612',
    followers: '24.1k',
    following: '318',
    avatar:
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/641ws1q8_DSC04994.JPG',
    grid: [
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/3azgm29j_DSC04813.JPG',
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/641ws1q8_DSC04994.JPG',
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/iymgjt5i_DSC05017.JPG',
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/wtrcvoaq_DSC04820.JPG',
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/vd5vyqcl_DSC05093.JPG',
      'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/3azgm29j_DSC04813.JPG',
    ],
  },
};

export const NANCREATED = {
  name: 'Nancreated',
  tagline: 'Filming, then sharing.',
  kicker: 'Cinematography · Events · est. 2021',
  link: 'https://www.instagram.com/nancreated/',
  linkLabel: '@nancreated',
  sub:
    'I started filming because I could not explain things in words. Now I do both. A camera is how I notice things. Screenings are how I share them. The hardest part is knowing when something is worth recording.',
  subtitle: 'Short films · brand films · screenings · supper clubs',
  heroImage: 'https://images.unsplash.com/photo-1607276159787-9ef4db5c0d0b',
  heroAlt: 'Cinema camera on tripod, editorial lighting',
  stats: [
    { label: 'body of work', value: '40+', unit: 'films since 2021' },
    { label: 'gatherings', value: '18', unit: 'events in Bangkok and Boston' },
  ],
  quote: {
    body:
      'Every shot is a small bet on what someone will feel three seconds in. Most of the job is not pressing record until you know the bet.',
    emphasis: 'Most of the job is not pressing record.',
    attribution: 'Sirin, on what the camera teaches',
  },
  // Featured 9:16 reel (big right tile). Add src: '...mp4' to swap in.
  featuredReel: {
    src: 'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/0xvbannk_SnapInsta.to_AQPxrCDc9mNnw3Uzf7HS6FHqsrGZl3B-rpaqt1F3Rwlk5YnfCAj-0Kc3IrQC2Nwrkh1ZtL7fyCJSOwsiv__wC3zPA0JZFsDyyCvClxA.mp4',
    poster: null,
    label: 'Reel 01 · 2024',
    title: 'Grain Supper Club',
    duration: '01:48',
  },
  // Recent reels — 9:16 placeholders. Drop { src, poster } to fill.
  recentReels: [
    {
      src: 'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/y65jr82i_SnapInsta.to_AQP940iuMNI754N8p4or2YtrnBUyeQ0WuyGVkanZb5fiZ9w3GU4r05pkGaKYseTajBpjx6eTcpeq68_F9dQWrR_-nW9moHNkk4ZZJbc.mp4',
      poster: null,
      label: 'Reel 02',
      caption: 'Founder film · ’23',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/mvbj50p1_SnapInsta.to_AQOzqdMD3jxkMN6CH0u1oRXhYSCW7j0dtPoGPARXvQhqxYBUDk1rt4e0q2NTy0wYLV8JoqeNHiEkJJDTPwHJ04cg6kDBC9IBMdZ9QTs.mp4',
      poster: null,
      label: 'Reel 03',
      caption: 'Rooftop screening',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_rare-hybrid-operator/artifacts/84ayx2dq_SnapInsta.to_AQOrVJ7IT3ivwCUH0gcMTHGP8OHwOtRrsocvGVLlrotdc2aeC9Za1YUyFDviaXhgH2XyCCWtK39e6UuOnhubnj0ecIDySjjzQQ5N7WA.mp4',
      poster: null,
      label: 'Reel 04',
      caption: 'Short · Bangkok',
    },
  ],
  press: 'Selected for: Bangkok Film Week · a friend’s wedding · one very patient dog',
};


export const ABOUT = {
  body: [
    'I grew up in Thailand and moved to the US for college.',
    'Most of what I learned came from building things I needed or fixing things that felt off.',
  ],
  beliefsLabel: 'A few things I believe',
  beliefs: 'Most things do not fail because they are bad. They fail because no one sees them. Distribution is part of the product. Taste is learned. You notice what works enough times that you cannot ignore it. Launching something is not an announcement. It is deciding it is ready to be seen.',
};

export const ABOUT_NOTES = [
  { label: 'Where I studied', value: 'Tufts University - Computer Science; International School of Bangkok (ISB)' },
  { label: 'Where I work', value: 'Bangkok, Boston, and the 3am timezone' },
  { label: 'What motivates me', value: 'Creative projects and Läderach chocolate <3' },
];

export const MARQUEE_TERMS = [
  'GTM Engineer', 'Product Manager', 'Engineer',
  'Builder', 'Cinematographer', 'Macaron baker',
  'Storyteller', 'Someone who actually ships',
];

export const CONTACT_COPY = {
  headline: 'Building something early and real?',
  body:
    'If you are building something early and real, I will probably care about it. I like working with people who do not separate thinking from doing. Email me. I will respond.',
};

export const SOCIALS = [
  { label: 'Email', href: `mailto:${EMAIL}`, handle: EMAIL },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/sirin-thamakaison-410248b4/', handle: '/in/sirin-thamakaison-410248b4' },
  { label: 'Instagram', href: 'https://www.instagram.com/nancreated/', handle: '@nancreated' },
  { label: 'GitHub', href: 'https://github.com/SirinThamakaison', handle: '@SirinThamakaison' },
];


// Command palette entries. Each item dispatches an action via its href.
//   "#id"                 → scroll to section id
//   "#hero:<chipIndex>"   → open hero conversation chip (0..3)
//   "#work:<projectId>"   → focus a project in Selected Work
//   "mailto:"/"http"      → open link
export const COMMAND_ENTRIES = [
  {
    group: 'Navigate',
    items: [
      { cmd: 'Go to Selected Work', hint: 'work', href: '#work' },
      { cmd: 'Open BKKButter bento', hint: 'macarons', href: '#bkkbutter' },
      { cmd: 'Open Nancreated bento', hint: 'cinema · events', href: '#nancreated' },
      { cmd: 'Go to Practice', hint: 'practice', href: '#practice' },
      { cmd: 'Go to About', hint: 'about', href: '#about' },
      { cmd: 'Go to Contact', hint: 'say hi', href: '#contact' },
    ],
  },
  {
    group: 'Selected Work',
    items: [
      { cmd: 'Crying Cloud · youth mental health', hint: '100k+ reach', href: '#work:crying-cloud' },
      { cmd: 'WithU · Thailand’s #1 medical app', hint: '200k downloads', href: '#work:withu' },
      { cmd: 'Foundess · brand for women in startups', hint: 'creative director', href: '#work:foundess' },
      { cmd: 'Youth Neuro · neuroscience editorial', hint: 'contributor', href: '#work:youth-neuro' },
    ],
  },
  {
    group: 'Ask Sirin',
    items: [
      { cmd: 'Who are you?', hint: 'hero', href: '#hero:0' },
      { cmd: 'What have you built?', hint: 'hero', href: '#hero:1' },
      { cmd: 'What do you do?', hint: 'hero', href: '#hero:2' },
      { cmd: 'Why the name?', hint: 'hero', href: '#hero:3' },
    ],
  },
  {
    group: 'Reach out',
    items: [
      { cmd: 'Email Sirin', hint: 'n.thamakaison@gmail.com', href: `mailto:${EMAIL}?subject=Hey%20Sirin` },
      { cmd: 'LinkedIn', hint: '/in/sirin-thamakaison-410248b4', href: 'https://www.linkedin.com/in/sirin-thamakaison-410248b4/' },
      { cmd: 'Instagram', hint: '@nancreated', href: 'https://www.instagram.com/nancreated/' },
      { cmd: 'GitHub', hint: '@SirinThamakaison', href: 'https://github.com/SirinThamakaison' },
      { cmd: 'BKKButter on Instagram', hint: '@bkkbutter', href: 'https://www.instagram.com/bkkbutter/' },
    ],
  },
  {
    group: 'Easter eggs',
    items: [
      { cmd: 'What is she baking?', hint: 'spoiler: macarons', href: '#bkkbutter' },
      { cmd: 'Say it like Siri', hint: 'with an n', href: '#hero:3' },
      { cmd: 'Copy email to clipboard', hint: 'n.thamakaison@gmail.com', href: 'copy:email' },
    ],
  },
];
